<template>
  <div class="container-fluid mt-0 p-0">
    <!-- Header with Gradient -->
    <div class="header-gradient mb-4 px-4 py-5">
      <div style="max-width: 1000px; margin: 0 auto;">
        <h3 class="fw-bold text-white mb-1">🎲 정기권 당첨자 결과</h3>
      </div>
    </div>

    <div style="max-width: 1000px; margin: 0 auto; padding: 0 1rem;">
      <!-- 로딩 -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">조회 중...</span>
        </div>
        <p class="mt-3 text-muted">당첨자 목록을 조회 중입니다...</p>
      </div>

      <!-- 오류 -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <h5 class="alert-heading">오류 발생</h5>
        <p class="mb-0">{{ error }}</p>
      </div>

      <!-- 콘텐츠 -->
      <div v-else>
        <!-- 검색/필터 -->
        <div class="row g-2 mb-4">
          <div class="col-auto">
            <label class="form-label small fw-bold mb-1">주차장 선택</label>
            <select v-model="selectedAreaSeq" class="form-select form-select-sm" style="width: 250px;">
              <option value="">-- 모든 주차장 --</option>
              <option v-for="area in areas" :key="area.AreaSeq" :value="area.AreaSeq">
                {{ area.AreaName }}
              </option>
            </select>
          </div>
          <div class="col-auto">
            <label class="form-label small fw-bold mb-1">차량번호 검색</label>
            <input
              v-model="carNumberFilter"
              type="text"
              class="form-control form-control-sm"
              placeholder="차량번호 입력"
              style="width: 200px;"
            />
          </div>
        </div>

        <!-- 결과 없음 -->
        <div v-if="filteredGroups.length === 0" class="alert alert-info">
          <p class="mb-0">당첨자가 없습니다.</p>
        </div>

        <!-- 그룹별 당첨자 -->
        <div v-else class="mb-5">
          <div v-for="group in filteredGroups" :key="`${group.AreaSeq}-${group.GroupSeq}`" class="mb-4">
            <!-- 그룹 헤더 -->
            <div class="card shadow-sm border-0">
              <div class="group-header py-3 px-4">
                <h6 class="mb-0 fw-bold text-white">
                  📍 {{ group.AreaName }} / 🏘️ {{ group.GroupName }}
                </h6>
              </div>

              <!-- 당첨자 리스트 -->
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th class="text-start ps-4">성명</th>
                        <th class="text-start">차량번호</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="winner in group.winners" :key="winner.WaitingSeq" class="winner-row">
                        <td class="ps-4 py-3">
                          <strong>{{ maskName(winner.Name) }}</strong>
                        </td>
                        <td class="py-3">
                          <span class="font-monospace">{{ maskCarNumber(winner.CarNumber) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TicketResult',
  data() {
    return {
      loading: false,
      error: null,
      groups: [],
      selectedAreaSeq: '',
      carNumberFilter: ''
    }
  },
  computed: {
    areas() {
      // 모든 그룹에서 고유한 주차장 추출
      const areaMap = {};
      this.groups.forEach(g => {
        if (!areaMap[g.AreaSeq]) {
          areaMap[g.AreaSeq] = {
            AreaSeq: g.AreaSeq,
            AreaName: g.AreaName
          };
        }
      });
      return Object.values(areaMap).sort((a, b) => a.AreaSeq - b.AreaSeq);
    },
    filteredGroups() {
      let result = this.groups;
      
      // 주차장 필터
      if (this.selectedAreaSeq) {
        result = result.filter(g => g.AreaSeq == this.selectedAreaSeq);
      }
      
      // 차량번호 필터
      if (this.carNumberFilter.trim()) {
        const search = this.carNumberFilter.trim().toUpperCase();
        result = result.map(group => ({
          ...group,
          winners: group.winners.filter(w => 
            w.CarNumber && w.CarNumber.toUpperCase().includes(search)
          )
        })).filter(group => group.winners.length > 0);
      }
      
      return result;
    }
  },
  mounted() {
    this.fetchTicketResults();
  },
  methods: {
    async fetchTicketResults() {
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/ticket-results`);
        if (res.data.success && res.data.groups) {
          this.groups = res.data.groups;
        } else {
          this.error = '당첨자 데이터를 불러올 수 없습니다.';
        }
      } catch (e) {
        console.error('fetchTicketResults error:', e);
        this.error = e.response?.data?.message || '당첨자 목록을 조회할 수 없습니다.';
      } finally {
        this.loading = false;
      }
    },
    maskName(name) {
      if (!name) return '알수없음';
      const length = name.length;
      if (length <= 1) return '*';
      if (length === 2) return name[0] + '*';
      const first = name.slice(0, 1);
      const last = name.slice(-1);
      const stars = '*'.repeat(Math.max(1, length - 2));
      return `${first}${stars}${last}`;
    },
    maskCarNumber(carNumber) {
      if (!carNumber) return '';
      const trimmed = String(carNumber).trim();
      if (trimmed.length <= 2) return '*'.repeat(trimmed.length);
      return `${trimmed.slice(0, -2)}${'*'.repeat(2)}`;
    }
  }
}
</script>

<style scoped>
.header-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 0;
}

.group-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px 8px 0 0;
}

.card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-body {
  padding: 0;
}

.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  padding: 1rem;
  color: #495057;
  font-size: 0.9rem;
}

.table tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
  color: #212529;
}

.winner-row {
  transition: background-color 0.15s ease;
}

.winner-row:hover {
  background-color: #f8f9fa;
}

.font-monospace {
  font-family: 'Courier New', monospace;
  letter-spacing: 0.05em;
  color: #6c757d;
}

.table-responsive {
  border: none;
}

.form-label {
  color: #374151;
  font-size: 0.875rem;
}

.form-select-sm,
.form-control-sm {
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.form-select-sm:focus,
.form-control-sm:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

h3 {
  letter-spacing: -0.5px;
}
</style>
